import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import {
  CalendarCheck, CircleDollarSign, Receipt, TrendingUp, CalendarX, Lock, Hourglass, CheckCircle2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Badge from '../common/Badge';
import Button from '../common/Button';
import SaleEndModal from '../layout/SaleEndModal';

const formatRs = (n) => `Rs. ${(Number(n) || 0).toLocaleString()}`;

function ShiftRow({ seg }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-700/40 rounded-lg">
      <div>
        <p className="text-sm font-medium text-gray-900 dark:text-white">{seg.userName}</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 capitalize">{seg.role?.toLowerCase()}</p>
      </div>
      <div className="text-right space-y-0.5">
        <p className="text-sm font-semibold text-gray-900 dark:text-white">{formatRs(seg.sales)}</p>
        <p className="text-xs text-gray-400 dark:text-gray-500">
          {seg.orders} orders • {formatRs(seg.profit)} profit
        </p>
      </div>
    </div>
  );
}

export default function ShiftSummary() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { status, businessDate, summary, currentUser } = useSelector((state) => state.shift);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'admin';
  const isOpen = status === 'OPEN';
  const isClosed = status === 'CLOSED';
  const users = summary?.users || [];
  const myShift = users.find((u) => u.userId === user?.userId) || currentUser;

  if (status === 'NO_DAY' || !status) return null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              isClosed ? 'bg-gray-100 dark:bg-gray-700' : 'bg-emerald-50 dark:bg-emerald-900/20'
            }`}>
              {isClosed
                ? <CheckCircle2 size={20} className="text-gray-500 dark:text-gray-400" />
                : <Hourglass size={20} className="text-emerald-500" />}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                Business Session
                {isClosed ? (
                  <Badge variant="default" size="sm">Closed</Badge>
                ) : (
                  <Badge variant="success" size="sm">Open</Badge>
                )}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <CalendarCheck size={13} />
                {businessDate}
              </p>
            </div>
          </div>

          {isOpen && (
            <Button
              variant={isAdmin ? 'danger' : 'secondary'}
              size="sm"
              icon={CalendarX}
              onClick={() => {
                if (isAdmin) {
                  setConfirmOpen(true);
                } else {
                  toast('SALE END is available only to the admin/owner. Please contact the owner to end today\'s sale.', {
                    icon: '🔒',
                  });
                }
              }}
            >
              SALE END
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-5">
          <div className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 dark:bg-gray-700/40">
            <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
              <CircleDollarSign size={16} className="text-blue-500" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Day's Sales</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">{formatRs(summary?.totalSales)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 dark:bg-gray-700/40">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center flex-shrink-0">
              <TrendingUp size={16} className="text-emerald-500" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Day's Profit</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">{formatRs(summary?.totalProfit)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 dark:bg-gray-700/40">
            <div className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center flex-shrink-0">
              <Receipt size={16} className="text-purple-500" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Day's Orders</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">{summary?.totalOrders || 0}</p>
            </div>
          </div>
        </div>

        <div className="px-5 pb-5">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              {isAdmin ? 'Shift Breakdown' : 'My Shift'}
            </h4>
            {isOpen && !isAdmin && (
              <button
                onClick={() => navigate('/pos')}
                className="text-xs text-primary-600 dark:text-primary-400 font-medium hover:underline"
              >
                Make a Sale →
              </button>
            )}
          </div>

          {isAdmin ? (
            users.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {users.map((u) => (
                  <ShiftRow key={u.userId} seg={u} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500">No shift activity yet today.</p>
            )
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {myShift ? (
                <ShiftRow seg={myShift} />
              ) : (
                <p className="text-sm text-gray-400 dark:text-gray-500">Your shift has no activity yet.</p>
              )}
            </div>
          )}

          {isClosed && (
            <div className="mt-4 flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
              <Lock size={13} />
              Today's session is closed. Sales are no longer accepted.
            </div>
          )}
        </div>
      </motion.div>

      <SaleEndModal isOpen={confirmOpen} onClose={() => setConfirmOpen(false)} />
    </>
  );
}